import { customAlphabet } from "nanoid";
import type { LoginActivity, Payload, RequestMetadata, WorkerContext, WorkerEnv } from "../types";
import { ADMIN_API_PATH, ADMIN_TOKEN_NAME, defalutEmailPreferences, TOKEN_NAME } from "./const";
import { deleteCookie, getCookie } from "hono/cookie";
import { dbErrorSave } from "../db/query/logs";
import { setCookie } from "hono/cookie";
import { NOTIFICATION_TEMPLATES, NotificationType } from "./notifyTemplates";
import type { NotificationParamsMap } from "./notifyTemplates";

/**
 * 哈希密码（使用 SHA-256 + 随机盐）
 */ export async function hashPassword(password: string) {
  const encoder = new TextEncoder();
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const saltHex = Array.from(salt)
    .map(byte => byte.toString(16).padStart(2, "0"))
    .join("");
  const saltedPassword = encoder.encode(password + saltHex);
  const hashBuffer = await crypto.subtle.digest("SHA-256", saltedPassword);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(byte => byte.toString(16).padStart(2, "0")).join("");
  return `${saltHex}:${hashHex}`;
}

/**
 * 验证密码
 */
export async function verifyPassword(password: string, storedHash: string) {
  const [saltHex, hash] = storedHash.split(":");
  if (!saltHex || !hash) throw new Error("Invalid hash format");
  const encoder = new TextEncoder();
  const saltedPassword = encoder.encode(password + saltHex);
  const hashBuffer = await crypto.subtle.digest("SHA-256", saltedPassword);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const newHash = hashArray.map(byte => byte.toString(16).padStart(2, "0")).join("");
  return newHash === hash;
}

/**
 * 生成一个 32 字节的随机加密密钥（Base64 编码）
 */
export function generateEncryptionKey() {
  const keyBytes = crypto.getRandomValues(new Uint8Array(32)); // 32 字节（256 位）
  return btoa(String.fromCharCode(...keyBytes));
}

/**
 * 签发JWT
 */
export async function signJwt(payload: Payload, secret: string) {
  // 设置 JWT 的头部和负载
  const header = { alg: "HS256", typ: "JWT" };
  // 系统 30 天，其他用 expires_in
  const expiresIn = payload.login_type === "system" ? 3600 * 24 * 30 : payload.expires_in || 3600;
  const body = {
    ...payload,
    exp: Math.floor(Date.now() / 1000) + expiresIn, // 添加 exp（秒）
  };
  const encodedHeader = base64urlEncode(JSON.stringify(header));
  const encodedPayload = base64urlEncode(JSON.stringify(body));

  const key = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"]
  );
  const signature = await crypto.subtle.sign(
    "HMAC",
    key,
    new TextEncoder().encode(`${encodedHeader}.${encodedPayload}`)
  );
  return `${encodedHeader}.${encodedPayload}.${base64urlEncode(signature as ArrayBuffer)}`;
}

export function base64urlEncode(data: string | ArrayBuffer) {
  const buffer =
    data instanceof ArrayBuffer ? new Uint8Array(data) : new TextEncoder().encode(data);
  const base64 = btoa(String.fromCharCode(...buffer));
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

/**
 * 解析token
 */
export async function verifyJwt(token: string, secret: string) {
  const [encodedHeader, encodedPayload, encodedSignature] = token.split(".");
  if (!encodedHeader || !encodedPayload || !encodedSignature) {
    throw new Error("Invalid JWT format");
  }

  let header, payload: Payload;
  try {
    header = JSON.parse(new TextDecoder().decode(base64urlDecode(encodedHeader)));
    payload = JSON.parse(new TextDecoder().decode(base64urlDecode(encodedPayload)));
  } catch (e) {
    throw new Error("Invalid JWT encoding");
  }

  if (header.alg !== "HS256") {
    throw new Error("Unsupported algorithm");
  }

  const key = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["verify"]
  );
  const isValid = await crypto.subtle.verify(
    "HMAC",
    key,
    base64urlDecode(encodedSignature),
    new TextEncoder().encode(`${encodedHeader}.${encodedPayload}`)
  );

  if (!isValid) {
    throw new Error("Invalid signature");
  }

  // 检查 exp
  if (payload.exp && Date.now() > payload.exp * 1000) {
    throw new Error("Token expired");
  }

  return payload;
}

// 解码 base64url 格式
export function base64urlDecode(str: string) {
  let base64 = str.replace(/-/g, "+").replace(/_/g, "/");
  while (base64.length % 4 !== 0) base64 += "=";
  const binary = atob(base64);
  return Uint8Array.from(binary, c => c.charCodeAt(0));
}

/**
 * 加密数据
 */
export async function encrypt(data: any, key: string) {
  // 确保密钥为 UTF-8 编码的字符串
  const keyBytes = Uint8Array.from(atob(key), c => c.charCodeAt(0));
  if (keyBytes.length !== 32) {
    throw new Error("Invalid encryption key length: must be 32 bytes when decoded from Base64");
  }

  // 导入密钥（使用 AES-GCM）
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    keyBytes,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt"]
  );

  // 生成 IV（初始化向量，12 字节）
  const iv = crypto.getRandomValues(new Uint8Array(12));

  // 加密数据
  const encodedData = new TextEncoder().encode(data);
  const encrypted = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, cryptoKey, encodedData);

  // 组合 IV 和加密数据为 Buffer，然后转为 Base64
  const combined = new Uint8Array(iv.length + encrypted.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(encrypted), iv.length);
  return btoa(String.fromCharCode(...combined));
}

/**
 * 解密数据
 */
export async function decrypt(encryptedData: string, key: string) {
  const keyBytes = Uint8Array.from(atob(key), c => c.charCodeAt(0));
  if (keyBytes.length !== 32) {
    throw new Error("Invalid encryption key length: must be 32 bytes when decoded from Base64");
  }

  const combined = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));
  const iv = combined.subarray(0, 12);
  const encrypted = combined.subarray(12);

  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    keyBytes,
    { name: "AES-GCM", length: 256 },
    false,
    ["decrypt"]
  );

  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: new Uint8Array(iv) },
    cryptoKey,
    encrypted
  );

  return new TextDecoder().decode(decrypted);
}

export async function getHashEmail(id: string, prefix = "tt") {
  // 使用 crypto.subtle.digest 生成 MD5 哈希
  const encoder = new TextEncoder();
  const data = encoder.encode(id);
  const hashBuffer = await crypto.subtle.digest("MD5", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const shortHash = hashArray
    .map(b => b.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, 8);
  return `${prefix}_${shortHash}@example.com`;
}

/**
 * 提取请求元数据
 * @param c Hono上下文
 * @returns 元数据对象
 */
export function extractRequestMetadata(c: WorkerContext) {
  // 提取请求信息
  const ip =
    c.req.header("CF-Connecting-IP") ||
    c.req.header("X-Forwarded-For") ||
    c.req.header("X-Real-IP") ||
    "Unknown";

  // 从Cloudflare数据中获取更准确的位置信息
  const cf = (c.req.raw as any).cf || {};
  const location = cf.country || c.req.header("CF-IPCountry") || "Unknown";
  const userAgent = c.req.header("User-Agent") || "Unknown";

  // 更准确地判断客户端类型
  const referer = c.req.header("Referer");
  const clientTypeHeader = c.req.header("X-Client-Type");
  let client = "Unknown";
  if (clientTypeHeader) {
    client = clientTypeHeader;
  } else if (referer && referer.includes("localhost")) {
    client = "Local Development";
  } else if (referer) {
    client = "Web";
  } else if (
    userAgent.includes("api-client") ||
    userAgent.includes("axios") ||
    userAgent.includes("postman")
  ) {
    client = "API Client";
  } else {
    client = "Direct Access";
  }

  // 更准确地判断设备类型
  let device = "Unknown";
  const deviceMatch = userAgent.match(
    /(Mobile|Tablet|iPad|Android|Windows|Macintosh|Linux|iPhone|iOS)/i
  );
  if (deviceMatch) {
    if (deviceMatch[1] === "iPad" || deviceMatch[1] === "Tablet") {
      device = "Tablet";
    } else if (
      deviceMatch[1] === "Mobile" ||
      deviceMatch[1] === "Android" ||
      deviceMatch[1] === "iPhone" ||
      deviceMatch[1] === "iOS"
    ) {
      device = "Mobile";
    } else {
      device = "Desktop";
    }
  } else {
    device = "Desktop"; // 默认为桌面设备
  }

  // 提取 Cloudflare 提供的地理和网络信息
  const continent = cf.continent || "Unknown";
  const country = cf.country || "Unknown";
  const region = cf.region || "Unknown";
  const city = cf.city || "Unknown";
  const timezone = cf.timezone || "Unknown";
  const latitude = cf.latitude || "Unknown";
  const longitude = cf.longitude || "Unknown";
  const asn = cf.asn || "Unknown";
  const colo = cf.colo || "Unknown";
  const botManagement = cf.botManagement || {};

  // 先获取headers entries结果，避免多次遍历
  const headerEntries = Array.from(c.req.raw.headers.entries());

  // 提取更多请求头信息
  const origin = c.req.header("Origin") || null;
  const host = c.req.header("Host") || "Unknown";
  const accept = c.req.header("Accept") || "";
  const contentType = c.req.header("Content-Type") || "";
  const acceptLanguage = c.req.header("Accept-Language") || null;
  const acceptEncoding = c.req.header("Accept-Encoding") || null;

  // 获取所有请求头
  const allHeaders: Record<string, string> = {};
  for (const [key, value] of headerEntries) {
    allHeaders[key] = value;
  }

  return {
    // 基本网络信息
    ip_address: ip,
    location,
    device,
    client,
    method: c.req.method,
    response_time: 0,
    userAgent,
    headers: allHeaders,

    // Cloudflare 提供的详细地理和网络信息
    continent,
    country,
    region,
    city,
    timezone,
    latitude,
    longitude,
    asn,
    colo,

    // Bot管理信息
    botScore: botManagement.score || null,
    botManagement: botManagement ? JSON.stringify(botManagement) : null,

    // HTTP请求信息
    referer,
    origin,
    host,
    accept,
    contentType,

    // HTTP协议信息
    httpProtocol: cf.httpProtocol || "Unknown",
    tlsVersion: cf.tlsVersion || "Unknown",
    tlsCipher: cf.tlsCipher || "Unknown",

    // 安全相关信息
    isSuspicious: false,
    suspiciousReasons: [] as string[],
    acceptLanguage,
    acceptEncoding,
  } as RequestMetadata;
}

/**
 * 检测恶意请求
 * @param metadata 请求元数据
 * @returns 是否为可疑请求及原因
 */
export function detectSuspiciousRequest(metadata: any) {
  let isSuspicious = false;
  const suspiciousReasons: string[] = [];

  // 检测非常规User-Agent
  if (metadata.userAgent === "Unknown") {
    isSuspicious = true;
    suspiciousReasons.push("Missing User-Agent");
  } else if (metadata.userAgent.length < 10) {
    isSuspicious = true;
    suspiciousReasons.push("Unusually short User-Agent: " + metadata.userAgent);
  }

  // 检测缺失必要的头部（但允许本地开发环境）
  if (
    !metadata.referer &&
    !metadata.origin &&
    metadata.client !== "Local Development" &&
    metadata.client !== "API Client"
  ) {
    suspiciousReasons.push("Missing Referer and Origin headers");
  }

  // 检测异常的Accept头部（仅在需要时检查）
  if (
    metadata.accept &&
    !metadata.accept.includes("application/json") &&
    !metadata.accept.includes("*/*") &&
    !metadata.accept.includes("text/plain")
  ) {
    suspiciousReasons.push("Unusual Accept header: " + metadata.accept);
  }

  return { isSuspicious, suspiciousReasons };
}

/**
 * 构造Durable Object内部URL的helper函数
 * @param path 相对路径
 * @returns 完整的DO内部URL
 */
export function doUrl(path: string) {
  // 确保路径以/开头
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `https://do.internal${normalizedPath}`;
}

/**
 * 检查邮箱验证重发限制
 * @param env 环境变量
 * @param email 邮箱地址
 * @param type verification | password_reset
 * @returns 是否可以重发验证邮件
 */
export async function checkResendVerificationLimit(
  env: WorkerEnv,
  email: string,
  type = "verification"
): Promise<{ canResend: boolean; count: number; error?: string }> {
  try {
    const resendTrackerId = env.RESEND_TRACKER_DO.idFromName("resend-tracker");
    const resendTrackerStub = env.RESEND_TRACKER_DO.get(resendTrackerId);

    // 构造DO的URL，传递email作为查询参数
    const url = `${doUrl("email")}?email=${encodeURIComponent(email)}&type=${type}`;

    // 检查是否可以发送（GET请求）
    const response = await resendTrackerStub.fetch(url, { method: "GET" });
    const result = (await response.json()) as any;

    if (response.status === 200) {
      return {
        canResend: result.canResend,
        count: result.count,
      };
    } else {
      return {
        canResend: false,
        count: result.count || 0,
        error: result.error || "Unknown error",
      };
    }
  } catch (error) {
    console.error("Failed to check resend verification limit:", error);
    // 出错时允许请求通过，避免服务中断
    return { canResend: true, count: 0 };
  }
}

/**
 * 记录邮箱验证重发行为
 * @param env 环境变量
 * @param email 邮箱地址
 * @param type verification | password_reset
 * @returns 记录结果
 */
export async function recordResendVerification(
  env: WorkerEnv,
  email: string,
  type = "verification"
): Promise<{ success: boolean; count?: number; error?: string }> {
  try {
    const resendTrackerId = env.RESEND_TRACKER_DO.idFromName("resend-tracker");
    const resendTrackerStub = env.RESEND_TRACKER_DO.get(resendTrackerId);

    // 构造DO的URL，传递email作为查询参数
    const url = `${doUrl("email")}?email=${encodeURIComponent(email)}&type=${type}`;

    // 记录发送行为（POST请求）
    const response = await resendTrackerStub.fetch(url, { method: "POST" });
    const result = (await response.json()) as any;

    if (response.status === 200 && result.success) {
      return {
        success: true,
        count: result.count,
      };
    } else {
      return {
        success: false,
        error: result.error || "Failed to record resend attempt",
      };
    }
  } catch (error) {
    console.error("Failed to record resend verification:", error);
    // 出错时仍继续发送邮件
    return { success: false }; // 表示记录失败但不影响主流程
  }
}

/**
 * 邮箱格式验证函数
 * @param email 需要验证的邮箱地址
 * @returns 如果邮箱格式正确返回true，否则返回false
 */
export function isValidEmail(email: string): boolean {
  if (!email) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * 设置Cookie
 */
export function setCookies(c: WorkerContext, token: string, tokenName = TOKEN_NAME) {
  const maxAge = daysToS(7);
  setCookie(c, tokenName, token, {
    httpOnly: true,
    secure: true,
    sameSite: "None",
    maxAge: maxAge,
    path: "/",
  });
}

/**
 * 清除Cookie
 * @param c
 */
export function clearCookie(c: WorkerContext, tokenName = TOKEN_NAME) {
  deleteCookie(c, tokenName, {
    httpOnly: true,
    secure: true,
    sameSite: "None",
    path: "/",
  });
}

/**
 * 天数转换为秒数
 * @param d
 * @returns
 */
export const daysToS = (d: number) => d * 24 * 60 * 60;

/**
 * 天数转换为毫秒数
 * @param d
 * @returns
 */
export const daysToMs = (d: number) => daysToS(d) * 1000;

/**
 * 验证Turnstile
 */
export async function verifyTurnstile(env: WorkerEnv, token: string, ip?: string) {
  const { TURNSTILE_SECRET_KEY, ENV } = env;
  if (token === "XXXX.DUMMY.TOKEN.XXXX" || ENV !== "prod") return true;
  if (!TURNSTILE_SECRET_KEY) {
    console.error("TURNSTILE_SECRET_KEY is not configured");
    return false;
  }

  const VERIFY_URL = "https://challenges.cloudflare.com/turnstile/v0/siteverify";
  try {
    const res = await fetch(VERIFY_URL, {
      method: "POST",
      body: new URLSearchParams({
        secret: TURNSTILE_SECRET_KEY,
        response: token,
        remoteip: ip || "",
      }),
    });

    if (!res.ok) {
      console.error(`Turnstile verification failed with status: ${res.status}`);
      return false;
    }

    const data: any = await res.json();
    return data.success === true;
  } catch (error) {
    console.error("Error during Turnstile verification:", error);
    return false;
  }
}

/**
 * 获取用户ID
 */
export async function getUserStatusByC(c: WorkerContext) {
  try {
    let realPath: string = c.req.path;
    if (c.req.method === "POST") {
      // 只有在请求是JSON类型时才尝试解析JSON
      const contentType = c.req.header("Content-Type") || "";
      if (contentType.includes("application/json")) {
        const { path } = await c.req.json();
        realPath = path || c.req.path;
      }
    }
    const isAdminPath = realPath.startsWith(ADMIN_API_PATH);
    let userId: string | null = null;
    const authToken = getCookie(c, isAdminPath ? ADMIN_TOKEN_NAME : TOKEN_NAME);
    const data = isAdminPath ? c.get("admin") : c.get("user");
    userId = data?.user_id;
    // 有token，但无用户ID，则尝试验证token
    if (authToken && !userId) {
      const data = await verifyJwt(
        authToken,
        isAdminPath ? c.env.ADMIN_JWT_SECRET : c.env.JWT_SECRET
      );
      userId = data?.user_id;
    }
    return { userId, authToken };
  } catch (error) {
    console.error("Failed to get user status:", error);
    return { userId: null, authToken: null };
  }
}

export function extracLoginInfo(c: WorkerContext, userId?: string, loginActivitiesStr?: string) {
  const date = new Date().toISOString();

  // 提取请求元数据
  const metadata = extractRequestMetadata(c);

  // 检测是否为可疑请求
  const { isSuspicious, suspiciousReasons } = detectSuspiciousRequest(metadata);
  // 检查是否来自已知的可信设备
  let isTrustedDevice = false;
  const currentDeviceSignature = {
    ip: metadata.ip_address || "",
    userAgent: metadata.userAgent || "",
    device: metadata.device || "",
    client: metadata.client || "",
  };

  let loginActivities: Array<LoginActivity> = [];

  if (loginActivitiesStr) {
    try {
      loginActivities = JSON.parse(loginActivitiesStr);
    } catch (e) {
      // 解析出错时记录详细错误信息，但为了系统稳定性使用空数组
      // 同时将原始数据记录到日志中以便后续分析和恢复
      dbErrorSave(
        c.env.DB,
        "login_activities_parse_error",
        JSON.stringify(e || {}) +
          "Login activities parse error. Original data was: " +
          loginActivitiesStr,
        userId,
        c.get("requestId")
      );
      loginActivities = [];
    }
  }

  // 使用 some 方法简化查找逻辑，找到第一个匹配的可信设备即可
  isTrustedDevice = loginActivities.some(activity => {
    if (!activity.isTrusted) return false;
    const previousDeviceSignature = {
      ip: activity.ip,
      userAgent: activity.userAgent,
      device: activity.device || "",
      client: activity.client || "",
    };
    // 比较设备签名
    // IP地址匹配或者User-Agent匹配（处理IP可能变化的情况，如移动网络）
    // 确保至少有一个有效标识符匹配，避免空字符串匹配空字符串
    const hasValidIdentifier =
      (currentDeviceSignature.ip && previousDeviceSignature.ip) ||
      (currentDeviceSignature.userAgent && previousDeviceSignature.userAgent);

    if (!hasValidIdentifier) return false;

    return (
      (currentDeviceSignature.ip === previousDeviceSignature.ip ||
        currentDeviceSignature.userAgent === previousDeviceSignature.userAgent) &&
      (currentDeviceSignature.device === previousDeviceSignature.device ||
        (!currentDeviceSignature.device && !previousDeviceSignature.device)) &&
      (currentDeviceSignature.client === previousDeviceSignature.client ||
        (!currentDeviceSignature.client && !previousDeviceSignature.client))
    );
  });

  // 添加新的登录活动记录
  const newActivity: LoginActivity = {
    time: date,
    ip: metadata.ip_address || "",
    location: metadata.location || "",
    userAgent: metadata.userAgent || "",
    device: metadata.device || "",
    client: metadata.client || "",
    country: metadata.country || "",
    region: metadata.region || "",
    city: metadata.city || "",
    isp: metadata.asn || 0,
    isSuspicious,
    suspiciousReasons,
    httpProtocol: metadata.httpProtocol,
    tlsVersion: metadata.tlsVersion,
    botScore: metadata.botScore,
    id: generateID(),
    isTrusted: isTrustedDevice, // 如果是已知可信设备，则自动标记为可信
    isDeleted: 0,
  };
  // 保持最近10-20条登录记录或最近6个月的记录（以较宽松的条件为准）
  loginActivities.unshift(newActivity);

  // 计算6个月前的时间
  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

  // 先尝试保留最近6个月的记录
  const recentActivities = loginActivities.filter(
    activity => new Date(activity.time) > sixMonthsAgo
  );

  // 如果6个月内记录少于10条，则保留最近的10条
  if (recentActivities.length < 10) {
    loginActivities = loginActivities.slice(0, Math.min(20, loginActivities.length));
  } else {
    // 如果6个月内记录超过20条，则只保留20条
    loginActivities = recentActivities.slice(0, 20);
  }

  return loginActivities;
}

export function getTitleAndContent<T extends NotificationType>(
  type: T,
  titleParams: NotificationParamsMap[T]["title"],
  contentParams: NotificationParamsMap[T]["content"]
) {
  const template = NOTIFICATION_TEMPLATES[type];

  // 使用类型断言解决TypeScript联合类型推断问题
  const titleData = template.title(titleParams as any);
  const contentData = template.content(contentParams as any);

  return {
    title: titleData.en,
    content: contentData.en,
  };
}

export function parseEmailPrefs(email_preferences?: string | Record<NotificationType, boolean>) {
  if (!email_preferences) return defalutEmailPreferences as Record<NotificationType, boolean>;
  let prefs;
  if (typeof email_preferences === "string") {
    try {
      email_preferences = email_preferences.replace(/\\/g, "");
      prefs = JSON.parse(email_preferences) as Record<NotificationType, boolean>;
    } catch (e) {
      console.error("Failed to parse email_preferences:", e);
      prefs = { ...defalutEmailPreferences } as Record<NotificationType, boolean>;
    }
  } else {
    prefs = email_preferences as Record<NotificationType, boolean>;
  }
  return { ...defalutEmailPreferences, ...prefs } as Record<NotificationType, boolean>;
}

// 创建nanoid实例，只使用字母和数字字符
const localNanoid = customAlphabet(
  "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789",
  32
);

/**
 *  32位随机字符串
 */
export function generateID(size?: number): string {
  return localNanoid(size);
}

/**
 * 重试异步操作
 * @param operation 要执行的操作
 * @param maxRetries 最大重试次数，默认为3次
 * @param delay 重试间隔（毫秒），默认为100ms
 */
export async function retryAsync<T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 100
): Promise<T> {
  let lastError: Error | undefined;

  for (let i = 0; i <= maxRetries; i++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error as Error;

      // 如果不是最后一次重试，则等待一段时间后重试
      if (i < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  // 如果所有重试都失败了，则抛出最后一个错误
  if (lastError) {
    throw lastError;
  }
  throw new Error("Unknown error occurred during retry operation");
}

/**
 * 回滚已执行的操作
 */
export async function rollbackOperations(context: {
  executedOperations: Array<() => Promise<void>>;
}) {
  // 按相反顺序执行回滚操作
  for (let i = context.executedOperations.length - 1; i >= 0; i--) {
    try {
      await context.executedOperations[i]();
    } catch (rollbackError) {
      console.error("Error during rollback operation:", rollbackError);
      // 继续执行其他回滚操作
    }
  }
}

/**
 * 辅助函数：为HTML注入canonical标签
 */
export function injectCanonicalTag(html: string, canonicalUrl?: string): string {
  if (!html.includes("</head>") || !canonicalUrl) {
    return html; // 如果没有head标签，则直接返回原内容
  }
  const canonicalTag = `<link rel="canonical" href="${canonicalUrl}" />`;
  return html.replace("</head>", `  ${canonicalTag}\n  </head>`);
}

/**
 * 辅助函数：获取canonical URL
 */
export function getCanonicalUrl(c: WorkerContext) {
  const env = c.env.ENV;
  const brandUrl = c.env.BRAND_URL;

  // 只在生产环境中添加canonical标签
  if (env === "prod" && brandUrl) {
    const pathname = c.req.path;
    // 确保brandUrl不以/结尾，避免双斜杠
    const normalizedBrandUrl = brandUrl.endsWith("/") ? brandUrl.slice(0, -1) : brandUrl;
    return `${normalizedBrandUrl}${pathname}`;
  }
  // 非生产环境返回空
  return;
}

function injectRobotsMeta(html: string) {
  const robotsMeta = '<meta name="robots" content="noindex, follow">';
  // 在 head 标签中注入 meta 标签
  if (html.includes("<head>")) {
    html = html.replace("<head>", `<head>${robotsMeta}`);
  } else {
    // 如果没有 head 标签，则在 html 标签后添加
    html = html.replace(/<html([^>]*)>/, `<html$1><head>${robotsMeta}</head>`);
  }
  return html;
}

/**
 * 处理HTML响应并注入canonical标签
 */
export async function processHtmlResponse(
  c: WorkerContext,
  _res: any,
  noIndex = false
): Promise<Response> {
  const res = _res as Response;
  if (res && res.headers.get("content-type")?.includes("text/html")) {
    const html = await res.text();
    const canonicalUrl = getCanonicalUrl(c);
    let modifiedHtml = injectCanonicalTag(html, canonicalUrl);
    if (noIndex) {
      modifiedHtml = injectRobotsMeta(modifiedHtml);
    }
    return new Response(modifiedHtml, {
      ...res,
      headers: {
        "Content-Type": "text/html; charset=utf-8",
        ...Object.fromEntries(res.headers.entries()),
      },
    });
  }
  return res;
}

/**
 * 辅助函数：获取前端资源
 */
export async function handleFrontendResource(c: WorkerContext, noIndex = false): Promise<Response> {
  // 尝试获取请求的资源
  let res = await c.env.ASSETS?.fetch(c.req.url).catch(() => null);

  // 如果找不到资源，则返回index.html以支持SPA路由
  if (!res || res.status === 404) {
    const url = new URL(c.req.url);
    url.pathname = "/";
    res = await c.env.ASSETS?.fetch(url.toString());
  }

  // 确保返回一个有效的Response对象
  if (res) {
    return await processHtmlResponse(c, res, noIndex);
  }
  // 如果仍然无法获取资源，返回一个默认的响应
  const canonicalUrl = getCanonicalUrl(c);
  return c.html(
    `
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Application Error - ${c.env.BRAND || "TaxEcoBit"}</title>
          <link rel="canonical" href="${canonicalUrl}" />
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              min-height: 100vh;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            
            .error-container {
              background: white;
              border-radius: 12px;
              box-shadow: 0 20px 40px rgba(0,0,0,0.1);
              padding: 40px;
              text-align: center;
              max-width: 500px;
              width: 90%;
            }
            
            .error-icon {
              font-size: 48px;
              margin-bottom: 20px;
              color: #667eea;
            }
            
            h1 {
              color: #333;
              margin-bottom: 15px;
              font-size: 24px;
            }
            
            p {
              color: #666;
              line-height: 1.6;
              margin-bottom: 25px;
            }
            
            .retry-button {
              background: #667eea;
              color: white;
              border: none;
              padding: 12px 24px;
              border-radius: 6px;
              cursor: pointer;
              font-size: 16px;
              transition: background 0.3s ease;
            }
            
            .retry-button:hover {
              background: #5a6fd8;
            }
            
            .footer {
              margin-top: 30px;
              color: #999;
              font-size: 14px;
            }
          </style>
        </head>
        <body>
          <div class="error-container">
            <div class="error-icon">⚠️</div>
            <h1>Application Error</h1>
            <p>Unable to load the application. This might be due to a temporary issue or maintenance.</p>
            <button class="retry-button" onclick="location.reload()">Retry</button>
            <div class="footer">
              If the problem persists, please contact support.
            </div>
          </div>
        </body>
      </html>
    `,
    500
  );
}
